import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { Modal } from "@mapmycustomers/ui";

import UsersSelect from "@app/component/UsersSelect";
import { shareEmailTemplate } from "@app/store/email/actions";
import { getCurrentUserId } from "@app/store/iam";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import messages from "./messages";
import styles from "./TemplateShareModal.module.scss";

interface TemplateShareModalProps {
  currentUserId: User["id"];
  onHide: () => void;
  onShare: typeof shareEmailTemplate.request;
  teams: Team[];
  template: EmailTemplate;
  users: User[];
}

const TemplateShareModal: FC<TemplateShareModalProps> = ({
  currentUserId,
  onHide,
  onShare,
  teams,
  template,
  users,
}) => {
  const intl = useIntl();

  const filteredUsers = useMemo(
    () => users.filter((user) => user.id !== currentUserId),
    [currentUserId, users]
  );

  const handleApply = useCallback(
    (userIds: User["id"][]) => {
      onShare({ templateId: template.id, userIds });
      onHide();
    },
    [template, onHide, onShare]
  );

  return (
    <Modal
      className={styles.modal}
      footer={null}
      onCancel={onHide}
      open
      subtitle={intl.formatMessage(messages.shareTemplateModalSubTitle)}
      title={intl.formatMessage(messages.shareTemplateModalTitle)}
      width="auto"
    >
      <UsersSelect
        allowEmpty
        allUsersWhenEmpty={false}
        onApply={handleApply}
        onCancel={onHide}
        selectedUserIds={template.userIds}
        teams={teams}
        users={filteredUsers}
      />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  teams: getTeams(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  onShare: shareEmailTemplate.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(TemplateShareModal);
